// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import translate from "@insite/client-framework/Translate";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getSndProductsAutocompleteModel from "@store/Framework/Store/Store/Components/ScratchAndDentForm/Handlers/GetSndProductsAutocompleteModel";
import setProduct from "@store/Framework/Store/Store/Components/ScratchAndDentForm/Handlers/SetProduct";
import { getProductSelector } from "@store/Framework/Store/Store/Components/ScratchAndDentForm/ScratchAndDentFormSelectors";
import { options } from "@toolkit/Framework/Common/GoogleAnalytics/mappers";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    label?: string;
    type?: "create" | "edit";
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching } = getProductSelector(state);

    return {
        isSearching,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    getSndProductsAutocompleteModel,
    setProduct,
};

export interface ScratchAndDentProductSearchStyles {
    wrapper?: SearchFieldWrapperStyles;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
}

export const scratchAndDentProductSearchStyles: ScratchAndDentProductSearchStyles = {
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: ${pxToRem(50)};
            height: ${pxToRem(50)};
            margin-right: ${pxToRem(10)};
        `,
    },
    productImage: {
        width: pxToRem(50),
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: ${pxToRem(5)};
        `,
    },
};
const styles = scratchAndDentProductSearchStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ScratchAndDentProductSearch = ({
    isSearching,
    label,
    type = "create",
    getAutocompleteModel,
    getSndProductsAutocompleteModel,
    setProduct,
}: Props) => {
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [filteredLines, setFilteredLines] = useState(options);

    const onSelectionChangeHandler = (value?: string) => {
        setProduct({ productId: value });
    };

    useEffect(() => {
        if (type === "edit" && options.length === 0) {
            getSndProductsAutocompleteModel({
                query: "",
                onSuccess: result => {
                    if (!result) {
                        return;
                    }
                    const newOptions = result.map(product => ({
                        optionText: product.productNumber,
                        optionValue: product.id || undefined,
                        rowChildren: (
                            <StyledWrapper {...styles.optionWrapper}>
                                <StyledWrapper {...styles.imageWrapper}>
                                    <LazyImage {...styles.productImage} src={product.mediumImagePath} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.autocompleteTitleText}>{product.productNumber}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        ),
                    }));
                    setOptions(newOptions);
                },
                onComplete(resultProps) {
                    if (resultProps?.apiResult) {
                        this.onSuccess?.(resultProps?.apiResult);
                    }
                },
            });
        }
    }, []);

    const debouncedSearchProducts = debounce((query: string) => {
        getAutocompleteModel({
            query,
            onSuccess: result => {
                if (!result?.products) {
                    return;
                }

                const newOptions = result.products.map(product => ({
                    optionText: product.erpNumber,
                    optionValue: product.id || undefined,
                    rowChildren: (
                        <StyledWrapper {...styles.optionWrapper}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage {...styles.productImage} src={product.image} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                                <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ),
                }));
                setOptions(newOptions);
            },
            onComplete(resultProps) {
                if (resultProps?.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps?.apiResult);
                }
            },
        });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        if (type === "create") {
            debouncedSearchProducts(event.target.value);
        } else {
            setFilteredLines(
                options.filter(
                    o =>
                        o.optionText.toLowerCase().includes(event.target.value.toLowerCase()) ||
                        o.optionValue?.toLowerCase().includes(event.target.value.toLowerCase()),
                ),
            );
        }
    };

    return (
        <SearchFieldWrapper extendedStyles={styles.wrapper}>
            <DynamicDropdown
                {...styles.searchDynamicDropdown}
                label={label}
                onSelectionChange={onSelectionChangeHandler}
                onInputChange={onInputChanged}
                filterOption={() => true}
                options={type === "create" ? options : filteredLines}
                placeholder={translate("Enter keyword or item #")}
                isLoading={isSearching}
            />
        </SearchFieldWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ScratchAndDentProductSearch);
